import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "../../lib/api";
import { Package, ShoppingCart, Stethoscope, Globe, Calendar, Users, Banknote, AlertCircle, Clock, ChevronRight, Trophy, TrendingUp } from "lucide-react";

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [alerts, setAlerts] = useState({ threshold: 5, items: [] });
    const [leaderboard, setLeaderboard] = useState(null);

    useEffect(() => {
        api.get("/admin/stats").then(r => setStats(r.data)).catch(() => {});
        api.get("/admin/stock/alerts").then(r => setAlerts(r.data)).catch(() => {});
        api.get("/admin/mr-leaderboard").then(r => setLeaderboard(r.data)).catch(() => {});
    }, []);

    if (!stats) return <div className="text-sm text-stone-500">Loading…</div>;

    const cards = [
        { label: "Revenue (orders)", value: formatINR(stats.revenue), icon: Banknote },
        { label: "Orders", value: stats.orders, icon: ShoppingCart },
        { label: "Products", value: stats.products, icon: Package },
        { label: "Registered Doctors", value: stats.doctors_registered, icon: Stethoscope },
        { label: "Internet Doctors", value: stats.internet_doctors, icon: Globe },
        { label: "Appointments", value: stats.appointments, icon: Calendar },
        { label: "Users", value: stats.users, icon: Users },
    ];
    return (
        <div data-testid="admin-dashboard">
            <div className="label-eyebrow text-sky-700">Overview</div>
            <h1 className="font-serif text-4xl text-slate-900 mt-2 mb-8">Dashboard</h1>

            {/* Pending sign-ups banner — only shows when there's something to act on */}
            {stats.pending_signups > 0 && (
                <Link
                    to="/admin/users"
                    className="block mb-6 bg-amber-50 border border-amber-300 hover:border-amber-500 hover:bg-amber-100/70 transition-colors px-5 py-4 group"
                    data-testid="dashboard-pending-banner"
                >
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-amber-500 text-white grid place-items-center font-serif text-lg shrink-0" data-testid="dashboard-pending-count">{stats.pending_signups}</div>
                            <div>
                                <div className="font-medium text-amber-900">
                                    {stats.pending_signups} staff sign-up{stats.pending_signups === 1 ? "" : "s"} awaiting your approval
                                </div>
                                <div className="text-xs text-amber-800/80 mt-0.5 flex items-center gap-1.5">
                                    <Clock className="w-3 h-3" />
                                    They can't sign in until you approve. Click to review.
                                </div>
                            </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-amber-700 group-hover:translate-x-1 transition-transform" />
                    </div>
                </Link>
            )}

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {cards.map((c) => (
                    <div key={c.label} className="bg-white border border-sky-100 p-5">
                        <div className="flex items-center justify-between">
                            <div className="label-eyebrow text-stone-500">{c.label}</div>
                            <c.icon className="w-4 h-4 text-sky-700" />
                        </div>
                        <div className="font-serif text-3xl text-slate-900 mt-2">{c.value}</div>
                    </div>
                ))}
            </div>

            {/* Low-stock alerts */}
            <div className="mt-10" data-testid="dashboard-low-stock">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="font-serif text-2xl text-slate-900 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                        Low stock <span className="text-sm text-stone-500">(at or below {alerts.threshold})</span>
                    </h2>
                    <Link to="/admin/stock" className="text-sm underline text-sky-700">Open stock manager →</Link>
                </div>
                {alerts.items.length === 0 ? (
                    <div className="bg-white border border-sky-100 p-6 text-sm text-stone-500 text-center">All products comfortably above the threshold. ✨</div>
                ) : (
                    <div className="bg-white border border-amber-200 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead><tr className="bg-amber-50 border-b border-amber-200">
                                <th className="text-left p-3 text-xs uppercase tracking-widest text-amber-900">Product</th>
                                <th className="text-left p-3 text-xs uppercase tracking-widest text-amber-900">Size</th>
                                <th className="text-left p-3 text-xs uppercase tracking-widest text-amber-900">SKU</th>
                                <th className="text-right p-3 text-xs uppercase tracking-widest text-amber-900">Stock left</th>
                            </tr></thead>
                            <tbody>
                                {alerts.items.map((it) => (
                                    <tr key={`${it.product_id}-${it.size_id}`} className="border-b border-stone-100" data-testid={`low-row-${it.product_id}`}>
                                        <td className="p-3 font-medium text-slate-900">{it.product_name}</td>
                                        <td className="p-3 text-stone-600">{it.size_label}</td>
                                        <td className="p-3 text-xs font-mono text-stone-500">{it.sku || "—"}</td>
                                        <td className={`p-3 text-right font-mono font-medium ${it.stock <= 2 ? "text-red-700" : "text-amber-700"}`}>{it.stock}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* MR Performance Leaderboard */}
            {leaderboard && leaderboard.rows && leaderboard.rows.length > 0 && (
                <div className="mt-8" data-testid="mr-leaderboard-widget">
                    <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
                        <div>
                            <div className="label-eyebrow text-sky-700 flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" />MR Performance</div>
                            <h2 className="font-serif text-2xl text-slate-900 mt-1">Leaderboard · {leaderboard.month}</h2>
                            <p className="text-xs text-stone-500 mt-1">Composite score weights revenue, orders, CRM activity, and DRS visits this month.</p>
                        </div>
                        <Link to="/admin/users" className="text-xs text-sky-700 hover:underline inline-flex items-center">
                            Manage MR accounts <ChevronRight className="w-3 h-3 ml-0.5" />
                        </Link>
                    </div>
                    <div className="bg-white border border-sky-100 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-sky-50 border-b border-sky-100">
                                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Rank</th>
                                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">MR</th>
                                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Territory</th>
                                    <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Orders MTD</th>
                                    <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Revenue MTD</th>
                                    <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">CRM contacts</th>
                                    <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Ordering</th>
                                    <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Activities</th>
                                    <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">DRS</th>
                                    <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Score</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leaderboard.rows.slice(0, 10).map((m, i) => (
                                    <tr key={m.mr_id} className="border-b border-stone-100 hover:bg-sky-50/40" data-testid={`leaderboard-row-${m.mr_id}`}>
                                        <td className="p-3 font-mono">
                                            {i === 0 ? <span className="text-amber-500 font-bold text-base">🏆 1</span>
                                                : i === 1 ? <span className="text-stone-400 font-bold">🥈 2</span>
                                                    : i === 2 ? <span className="text-amber-700 font-bold">🥉 3</span>
                                                        : <span className="text-stone-400">{i + 1}</span>}
                                        </td>
                                        <td className="p-3 font-medium text-slate-900">
                                            {m.mr_name}
                                            <div className="text-[10px] text-stone-500 font-normal">{m.mr_email}</div>
                                        </td>
                                        <td className="p-3 text-xs text-stone-600">{m.territory || "—"}</td>
                                        <td className="p-3 text-right font-mono">{m.orders_mtd}</td>
                                        <td className="p-3 text-right font-mono text-emerald-700 font-semibold">{formatINR(m.revenue_mtd)}</td>
                                        <td className="p-3 text-right font-mono">{m.crm_contacts}</td>
                                        <td className="p-3 text-right font-mono text-sky-700 font-semibold">{m.crm_ordering}</td>
                                        <td className="p-3 text-right font-mono">{m.crm_activities_mtd}</td>
                                        <td className="p-3 text-right font-mono">{m.drs_mtd}</td>
                                        <td className="p-3 text-right font-mono text-amber-700 font-bold">
                                            <TrendingUp className="w-3 h-3 inline mr-0.5" />{m.score}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
